import type { Dispatch, SetStateAction } from 'react'
import type { RefObject } from 'react'
import type { MapRef } from 'react-map-gl/maplibre'

export type SiteCategory = 'OTHER_AT' | 'RESEARCH_LAB' | 'SMART_LIVING' | 'HOUSING' | 'AT_RESOURCE_CENTER'
export type Region = 'Western PA' | 'Central PA' | 'Eastern PA' | 'Statewide'
export type DataSource = 'web_verified' | 'inglis_website' | 'spreadsheet_pending'
export type ViewMode = 'map' | 'list'
export type FilterGroupKey = 'categories'

export interface FamilyAudienceContent {
  headline?: string
  description?: string
  accessChip?: string
  highlights?: string[]
  cta?: string
}

export interface SiteAudiences {
  family?: FamilyAudienceContent
}

export interface ReuseSite {
  /** No separate id field — this doubles as the React list key and the SitePanel expand/collapse
   *  Set key (see renderSubSiteItem in SitePanel.tsx), so it must stay unique across
   *  reuseSites.json (enforced by reuseSites.test.ts). */
  name: string
  lat: number
  lon: number
  contact: string | null
  phone: string | null
  email: string | null
  url: string | null
  description?: string
  highlights?: string[]
  cta?: string
}

export interface Site {
  id: string
  name: string
  org: string
  category: SiteCategory
  level1_function: string
  level2_access: string
  region: Region
  city: string
  address: string
  lat: number
  lon: number
  url: string
  contact?: string
  phone?: string
  dataSource: DataSource
  audiences: SiteAudiences
  /** Present only on the aggregated TechOWL Reuse Network pseudo-site — see reuseNetworkSite.ts. */
  subSites?: {
    mapUrl: string
    items: ReuseSite[]
  }
}

export interface SitesData {
  sites: Site[]
}

export interface Filters {
  categories: (SiteCategory | 'TECHOWL')[]
}

export interface FilterOption {
  value: string
  label: string
  swatch?: string
}

export interface FilterGroup {
  key: FilterGroupKey
  label: string
  options: FilterOption[]
}

export interface MarkerConfigEntry {
  color: string
  shape: 'circle' | 'diamond' | 'square' | 'house' | 'triangle'
  label: string
}

export function getSpokenDescription(audiences: SiteAudiences): string {
  return audiences.family?.description ?? ''
}

export interface MapContextValue {
  filters: Filters
  toggleFilter: (group: FilterGroupKey, value: string) => void
  resetFilters: () => void
  clearGroup: (group: FilterGroupKey) => void
  isDefaultFilters: boolean
  selectedSite: Site | null
  setSelectedSite: (site: Site | null) => void
  /** Id of the marker whose hover card is currently open, or null. Single shared value so only one
   *  card can ever be open — see MapProvider and SiteMarker for why per-marker state didn't work. */
  hoveredSiteId: string | null
  setHoveredSiteId: Dispatch<SetStateAction<string | null>>
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  audioGuide: boolean
  setAudioGuide: (updater: boolean | ((prev: boolean) => boolean)) => void
  mapRef: RefObject<MapRef | null>
}
